import React from 'react';
import TransferLinkedCapitalActionCard from './TransferLinkedCapitalActionCard';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { CalendarDay } from 'further-ui/utils';
import { LinkedCapitalAction } from 'further-types/lp/capital-action';
import EqualisationsTableHeader from 'components/EqualisationElements/EqualisationsTableHeader';
import { Button, Grid, Typography } from '@mui/material';
import GridContainer from 'components/GridContainer';
import ButtonGroup from 'components/ButtonGroup';
import Spacing from 'components/Spacing';
import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import { useFormContext } from 'react-hook-form';
import { PartnerTransfersFormValues } from '../../hooks/usePartnerTransfersForm';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';

interface TransferLinkedCapitalActionsContentProps {
  partnerTransferId: string;
  transferDate: CalendarDay;
  capitalActions: Array<LinkedCapitalAction>;
  baseCurrency: CurrencyCode;
  onNext: () => void;
  onPrevious: () => void;
}

const TransferLinkedCapitalActionsContent: React.FC<
  TransferLinkedCapitalActionsContentProps
> = ({
  partnerTransferId,
  transferDate,
  capitalActions,
  baseCurrency,
  onNext,
  onPrevious,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const { getValues } = useFormContext<PartnerTransfersFormValues>();

  const updatePartnerTransfer = api.partnerTransfers.update.useMutation();

  const handleNext = () => {
    const formValues = getValues();
    const input = {
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
      transferDate: formValues.transferDate,
      transferorCommitmentId: formValues.trasferrorCommitmentId,
      currentSection: PartnerTransferSection.LinkedCapitalActions,
      transferees: formValues.transferees.map((transferee) => ({
        investorId: transferee.investorId,
        amountLcy: transferee.amountLcy,
        percentage: transferee.percentage,
      })),
      saveActionLinkedCapitalActions: true,
    };
    updatePartnerTransfer.mutate(input, {
      onSuccess: () => {
        onNext();
      },
    });
  };

  const handlePrevious = () => {
    onPrevious();
  };

  const hasCapitalActions = capitalActions && capitalActions.length > 0;

  return (
    <GridContainer>
      <Grid size={{ xs: 12, lg: 12 }}>
        {!hasCapitalActions ? (
          <Spacing size="lg">
            <Typography variant="body1">
              No linked capital actions found for this partner transfer.
            </Typography>
          </Spacing>
        ) : (
          <>
            <EqualisationsTableHeader
              headers={['Name', 'Date', 'Amount']}
              gridTemplateColumns="60% 20% 20%"
            />
            {capitalActions.map((capitalAction, index) => (
              <TransferLinkedCapitalActionCard
                key={capitalAction.id}
                capitalAction={capitalAction}
                index={index}
                isLastElement={index === capitalActions.length - 1}
                baseCurrency={baseCurrency}
                transferDate={transferDate}
              />
            ))}
          </>
        )}
      </Grid>
      <Grid size={{ xs: 12, lg: 12 }}>
        <ButtonGroup>
          <Button
            variant="outlined"
            onClick={handlePrevious}
            disabled={updatePartnerTransfer.isPending}
          >
            Previous step
          </Button>

          <Button
            variant="contained"
            onClick={handleNext}
            disabled={updatePartnerTransfer.isPending}
            loading={updatePartnerTransfer.isPending}
          >
            Save and continue
          </Button>
        </ButtonGroup>
      </Grid>
    </GridContainer>
  );
};

export default TransferLinkedCapitalActionsContent;
